<template>
  <div class="py-4 container-fluid" v-if="state.colaboradores.length">
    <div class="row">
      <div
        class="col-4"
        v-for="colaborador in state.colaboradores"
        :key="colaborador.id"
      >
        <div class="card">
          <div class="d-flex justify-content-between">
            <h4 class="m-0">{{ colaborador.nome }}</h4>
            <button
              class="add-tarefa square-button"
              @click="openModalTarefa(colaborador.id)"
            >
              <i class="bi bi-plus"></i>
            </button>
          </div>

          <FlatPickr
            class="my-2"
            v-model="colaboradorData[colaborador.id]"
            :config="config"
          />
          <hr />
          <div class="d-flex flex-column gap-2">
            <template v-if="filteredTarefas(colaborador.id).length">
              <button
                v-for="tarefa in filteredTarefas(colaborador.id)"
                :key="tarefa.id"
                type="button"
                class="tarefa d-flex justify-content-between align-items-center p-3 rounded"
                @click="openModalTarefaInfos(tarefa)"
              >
                <div class="d-flex flex-column">
                  <div class="w-100 nome_tarefa">{{ tarefa.nome }}</div>
                </div>
                <div
                  class="action_buttons d-flex align-items-center flex-column gap-1"
                >
                  <input
                    type="checkbox"
                    :id="'checkbox-' + tarefa.id"
                    v-model="checkedTarefas[tarefa.id]"
                    @change="toggleTarefaStatus(tarefa)"
                    @click.stop
                    title="Alterar"
                    class="d-none"
                  />
                  <label
                    :for="'checkbox-' + tarefa.id"
                    class="checkbox-label"
                    @click.stop
                  >
                    <i
                      class="bi bi-check-square-fill"
                      :class="{ 'checked-icon': checkedTarefas[tarefa.id] }"
                    ></i>
                  </label>
                  <button
                    type="button"
                    class="btn-danger btn-sm mt-1 square-button"
                    @click.stop.prevent="deleteTarefa(tarefa)"
                  >
                    <i class="bi bi-trash3-fill"></i>
                  </button>
                </div>
              </button>
            </template>
            <template v-else>
              <div>Nenhuma tarefa para esta data.</div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <ModalTarefa
      v-if="modalTarefaVisible"
      :colaboradores="state.colaboradores"
      :colaboradorId="colaboradorIdForNewTask"
      @close="closeModalTarefa"
      @save="handleSave"
    >
      <template #footer>
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModalTarefa"
        >
          Fechar
        </button>
      </template>
    </ModalTarefa>

    <ModalTarefaInfos
      v-if="modalTarefaInfosVisible"
      :colaboradores="state.colaboradores"
      :tarefa="selectedTarefa"
      @close="closeModalTarefaInfos"
      @save="handleSave"
    />
  </div>
</template>

<script>
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { onMounted, reactive, ref, watch } from "vue";
import services from "../../services";

import { useStorage } from "vue3-storage";
import ModalTarefa from "../Modal/ModalTarefa.vue";
import ModalTarefaInfos from "../Modal/ModalTarefaInfos.vue";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

export default {
  components: { ModalTarefa, ModalTarefaInfos, FlatPickr },
  setup() {
    const storage = useStorage();
    const token = storage.getStorageSync("token");
    const id = storage.getStorageSync("id");
    const colaboradorData = reactive({});
    const filteredTarefasByColaborador = reactive({});

    const checkedTarefas = reactive({});

    const config = {
      locale: Portuguese,
      dateFormat: "Y/m/d",
    };

    const state = reactive({
      colaboradores: [],
    });
    const modalVisible = ref(false);

    const colaboradorIdForNewTask = ref(null);

    onMounted(() => {
      fetchColaborador();
    });

    watch(
      () => colaboradorData,
      (newVal, oldVal) => {
        for (const colaboradorId in newVal) {
          let newDate = newVal[colaboradorId];
          if (Array.isArray(newDate)) {
            newDate = newDate[0];
          }
          if (
            !oldVal ||
            !oldVal[colaboradorId] ||
            !(oldVal[colaboradorId] instanceof Date) ||
            (newDate instanceof Date &&
              oldVal[colaboradorId] instanceof Date &&
              newDate.getTime() !== oldVal[colaboradorId].getTime())
          ) {
            if (typeof newDate === "string") {
              newDate = new Date(newDate);
            }
            fetchTarefasPorData(colaboradorId, newDate);
          }
        }
      },
      { deep: true }
    );

    async function fetchColaborador() {
      try {
        const { data } = await services.colaboradores.getAll({ token });
        state.colaboradores = data;

        data.forEach((colab) => {
          colaboradorData[colab.id] = new Date();
          fetchTarefasPorData(colab.id, colaboradorData[colab.id]);
        });
      } catch (err) {
        console.error(err);
      }
    }

    async function fetchTarefasPorData(colaboradorId, selectedDate) {
      try {
        if (Array.isArray(selectedDate)) {
          selectedDate = selectedDate[0];
        }
        if (typeof selectedDate === "string") {
          selectedDate = new Date(selectedDate);
        }
        if (!(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
          filteredTarefasByColaborador[colaboradorId] = [];
          return;
        }

        const year = selectedDate.getFullYear();
        const day = String(selectedDate.getDate()).padStart(2, "0");
        const month = String(selectedDate.getMonth() + 1).padStart(2, "0");

        const formattedDate = `${year}-${month}-${day}`;

        const colaboradorIdNum = Number(colaboradorId);

        const { data } = await services.tarefas.tarefasPorDataColaborador({
          colaboradorId: colaboradorIdNum,
          dataSelecionada: formattedDate,
          token,
        });

        filteredTarefasByColaborador[colaboradorIdNum] = data.filter(
          (tarefa) => tarefa.colaborador_id === colaboradorIdNum
        );

        data.forEach((tarefa) => {
          if (!(tarefa.id in checkedTarefas)) {
            checkedTarefas[tarefa.id] = tarefa.status === "CONCLUIDO";
          }
        });
      } catch (err) {
        console.error(err);
        filteredTarefasByColaborador[colaboradorId] = [];
      }
    }

    const filteredTarefas = (colaboradorId) => {
      const tarefas = filteredTarefasByColaborador[colaboradorId] || [];
      let selectedDate = colaboradorData[colaboradorId];
      if (!selectedDate) return tarefas;

      if (Array.isArray(selectedDate)) {
        selectedDate = selectedDate[0];
      }
      if (typeof selectedDate === "string") {
        selectedDate = new Date(selectedDate);
      }
      if (!(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
        return tarefas;
      }

      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const selectedDateStr = `${year}-${month}-${day}`;

      return tarefas.filter((tarefa) => {
        const dateStr = (
          tarefa.created_at ||
          tarefa.data_criacao ||
          ""
        ).substring(0, 10);
        if (!dateStr) return false;
        return dateStr === selectedDateStr;
      });
    };

    const selectedTarefa = ref(null);
    const modalTarefaVisible = ref(false);
    const modalTarefaInfosVisible = ref(false);

    function openModalTarefa(colaboradorId = null) {
      colaboradorIdForNewTask.value = colaboradorId;
      modalTarefaVisible.value = true;
    }
    function closeModalTarefa() {
      modalTarefaVisible.value = false;
      colaboradorIdForNewTask.value = null;
      fetchColaborador();
    }

    function openModalTarefaInfos(tarefa) {
      selectedTarefa.value = tarefa;
      modalTarefaInfosVisible.value = true;
    }
    function closeModalTarefaInfos() {
      modalTarefaInfosVisible.value = false;
      selectedTarefa.value = null;
    }

    async function handleSave(tarefa) {
      const colaboradorId = storage.getStorageSync("colaborador_id");
      if (colaboradorId) {
        tarefa.colaborador_id = Number(colaboradorId);
      }

      console.log("Tarefa salva:", tarefa);
      closeModalTarefa();
      await fetchColaborador();
    }

    async function toggleTarefaStatus(tarefa) {
      try {
        const novoStatus =
          tarefa.status === "CONCLUIDO" ? "PENDENTE" : "CONCLUIDO";

        await services.tarefas.postById({
          id: tarefa.id,
          token,
          status: novoStatus,
        });

        const colaboradorId = tarefa.colaborador_id;
        const selectedDate = colaboradorData[colaboradorId];
        await fetchTarefasPorData(colaboradorId, selectedDate);
      } catch (error) {
        console.error(
          "Erro ao alterar status da tarefa:",
          error.response?.data || error
        );
      }
    }

    async function deleteTarefa(tarefa) {
      try {
        await services.tarefas.delete({ id: tarefa.id, token });
        const colaboradorId = tarefa.colaborador_id;
        const selectedDate = colaboradorData[colaboradorId];
        await fetchTarefasPorData(colaboradorId, selectedDate);
      } catch (error) {
        console.error(
          "Erro ao deletar a tarefa:",
          error.response?.data || error
        );
      }
    }

    return {
      state,
      colaboradorData,
      colaboradorIdForNewTask,
      config,
      modalTarefaVisible,
      modalTarefaInfosVisible,
      selectedTarefa,
      openModalTarefa,
      closeModalTarefa,
      openModalTarefaInfos,
      closeModalTarefaInfos,
      handleSave,
      filteredTarefas,
      checkedTarefas,
      toggleTarefaStatus,
      deleteTarefa,
    };
  },
};
</script>
<style scoped>
h5 {
  color: #202020;
}

tr {
  text-align: center;
}

th {
  color: #fff;
  border-bottom: solid 2px #202020 !important;
}

td {
  border-bottom: solid 1px #202020 !important;
  color: #202020;
}

.card {
  border: solid 2px #56008f;
  border-radius: 10px;
  background-color: #fff;
  padding: 1rem;
}

.card-tarefa:hover {
  transform: scale(0);
}

.btn {
  transform: none !important;
  margin-right: 5px;
  margin-bottom: 0;
  padding: 10px;
  width: 40px;
}

#btn_new_user {
  color: #202020;
  font-weight: 600;
  padding: 5px;
  border: solid 2px #202020;
  border-radius: 5px;
  background-color: rgb(152, 152, 152);
  transition: 0.2s;
}

#btn_new_user:hover {
  background-color: rgb(210, 209, 209);
}

.table-hover > tbody > tr:hover > * {
  color: #202020;
  background-color: rgb(225, 225, 225);
}

.add-tarefa {
  border: none;
  color: white;
  background-color: #56008f;
  aspect-ratio: 1 / 1;
  max-height: fit-content;
}

.tarefa {
  border: 1px solid #56008f;
  background-color: #f8eeff;
  transition: 0.15s ease-in-out;
}

.tarefa:hover {
  transform: scale(0.975);
}

.tarefa div {
  text-align: left;
}
.action_buttons button {
  border: none;
  transition: 0.15s ease-in-out;
}

.action_buttons label {
  font-size: 1.25rem;
  margin: 0;
}

.action_buttons i {
  color: #808080;
  width: 100%;
}

.checked-icon {
  color: #3dd03d !important;
}

.btn-danger {
  margin: 0;
  padding: 5px;
  max-height: fit-content;
}

.btn-danger i {
  color: white;
  width: fit-content;
}
</style>
